import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StaticPage.css';

function SecurityPage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="static-page">
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="nav-links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="/consulting">Consulting</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="btn-primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
      </nav>

      <section className="sp-hero">
        <div className="sp-container">
          <h1 className="sp-hero-title">Security</h1>
          <p className="sp-hero-text">
            Enterprise-grade security built into every layer of the CerebralMachines platform.
          </p>
        </div>
      </section>

      <section className="sp-section">
        <div className="sp-container">
          <h2 className="sp-section-title">Our Security Commitment</h2>
          <p className="sp-text">
            CerebralMachines is built for the most security-sensitive organizations in the world.
            From healthcare systems handling PHI to financial institutions managing regulatory compliance,
            our platform is architected to meet the highest standards of data protection, access control,
            and auditability.
          </p>
        </div>
      </section>

      <section className="sp-section sp-section-alt">
        <div className="sp-container">
          <h2 className="sp-section-title">Security Architecture</h2>
          <div className="sp-cards">
            <div className="sp-card">
              <h3>Encryption</h3>
              <p>
                All data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Customer encryption
                keys can be managed via customer-managed KMS for full control.
              </p>
            </div>
            <div className="sp-card">
              <h3>Zero Trust Architecture</h3>
              <p>
                Every request is authenticated and authorized. No implicit trust is granted based on
                network location. All access follows the principle of least privilege.
              </p>
            </div>
            <div className="sp-card">
              <h3>Identity &amp; Access Management</h3>
              <p>
                Enterprise IAM integration with SAML/OIDC SSO, role-based access control (RBAC),
                multi-factor authentication, and field-level access policies.
              </p>
            </div>
            <div className="sp-card">
              <h3>Audit &amp; Compliance Logging</h3>
              <p>
                Every action on the platform is logged with immutable audit trails. Full traceability
                of who accessed what data, when, and why — supporting regulatory requirements.
              </p>
            </div>
            <div className="sp-card">
              <h3>Network Security</h3>
              <p>
                VPC isolation, private endpoints, network segmentation, DDoS protection,
                and WAF integration. Supports air-gapped and sovereign deployment models.
              </p>
            </div>
            <div className="sp-card">
              <h3>Data Residency</h3>
              <p>
                Full control over where your data is stored and processed. Support for
                region-specific deployments to meet local data sovereignty requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-section">
        <div className="sp-container">
          <h2 className="sp-section-title">Compliance &amp; Certifications</h2>
          <p className="sp-text">
            CerebralMachines is designed to comply with the strictest industry standards and regulatory frameworks:
          </p>
          <div className="sp-cards">
            <div className="sp-card">
              <h3>HIPAA</h3>
              <p>Ready for clinical and PHI-sensitive workflows with BAA support.</p>
            </div>
            <div className="sp-card">
              <h3>SOC 2 Type II</h3>
              <p>Independently audited controls for security, availability, and confidentiality.</p>
            </div>
            <div className="sp-card">
              <h3>GDPR</h3>
              <p>Data protection by design with erasure support, consent management, and data portability.</p>
            </div>
            <div className="sp-card">
              <h3>Enterprise Ready</h3>
              <p>Designed to meet the security requirements of Fortune 500 organizations across regulated industries.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-section sp-section-alt">
        <div className="sp-container">
          <h2 className="sp-section-title">Security Practices</h2>
          <ul className="sp-list">
            <li>Regular penetration testing and vulnerability assessments by third-party security firms</li>
            <li>Secure software development lifecycle (SSDLC) with code review, SAST, and DAST</li>
            <li>Incident response plan with defined SLAs and communication protocols</li>
            <li>Employee security training and background checks</li>
            <li>Vendor risk management for all third-party services</li>
            <li>Business continuity and disaster recovery planning with regular testing</li>
          </ul>
        </div>
      </section>

      <section className="sp-section">
        <div className="sp-container">
          <h2 className="sp-section-title">Responsible Disclosure</h2>
          <p className="sp-text">
            If you discover a potential security vulnerability, we encourage responsible disclosure.
            Please report findings to our security team and we will investigate promptly.
          </p>
          <p className="sp-emphasis">security@cerebralmachines.ai</p>
        </div>
      </section>

      <section className="sp-cta">
        <div className="sp-container">
          <h2 className="sp-cta-title">Need More Details?</h2>
          <p className="sp-cta-text">
            Request our detailed security whitepaper or schedule a call with our security team.
          </p>
          <button className="btn-primary-large" onClick={() => navigate('/request-demo')}>Request a Demo</button>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
              <p className="footer-tagline">Building the cognitive backbone for enterprise AI.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <ul>
                  <li><a href="/platform">Platform</a></li>
                  <li><a href="/how-it-works">How it Works</a></li>
                  <li><a href="/use-cases">Use Cases</a></li>
                  <li><a href="/consulting">Consulting</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About</a></li>
                  <li><a href="/team">Team</a></li>
                  <li><a href="/careers">Careers</a></li>
                  <li><a href="/contact">Contact</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <ul>
                  <li><a href="/privacy">Privacy Policy</a></li>
                  <li><a href="/terms">Terms of Service</a></li>
                  <li><a href="/security">Security</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 CerebralMachines. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default SecurityPage;
